<template>
  <div class="w-full space-y-5">
    <!-- Top Client Overview Card -->
    <div
      v-if="client"
      class="flex flex-col gap-4 rounded-[1.4rem] border border-[#d8e4f0] bg-white p-5 sm:p-6 shadow-[0_10px_22px_rgba(15,23,42,0.04)] sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="flex items-center gap-4">
        <div class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#eaf3fb] text-lg font-black text-[#2f6fab]">
          {{ clientInitials }}
        </div>
        <div>
          <div class="flex items-center gap-2.5 flex-wrap">
            <h2 class="text-[1.4rem] font-black text-[#0f172a]">
              {{ client.company_name }}
            </h2>
            <span class="inline-flex rounded-full bg-[#eaf3fb] px-3 py-1 text-[0.72rem] font-extrabold uppercase tracking-[0.08em] text-[#2f6fab]">
              {{ client.client_id }}
            </span>
          </div>
          <p class="mt-1 text-[0.9rem] font-medium text-[#64748b]">
            Monthly performance reports
          </p>
        </div>
      </div>

      <div class="flex flex-col sm:items-end gap-2 shrink-0">
        <div class="text-[0.92rem] font-extrabold text-[#0f172a]">
          {{ generatedCount }} of {{ totalSlots }} ready
        </div>
        <div class="h-2.5 w-44 rounded-full bg-[#e2e8f0] overflow-hidden">
          <div
            class="h-full rounded-full bg-[#173b5f] transition-all duration-500"
            :style="{ width: progressPercentage + '%' }"
          />
        </div>
      </div>
    </div>

    <!-- Monthly Reports Section -->
    <section class="space-y-4">
      <div>
        <h2 class="text-[1.25rem] font-extrabold text-[#0f172a]">
          Monthly reports
        </h2>
        <p class="text-[0.9rem] text-[#64748b]">
          One report for each month.
        </p>
      </div>

      <AnimatedList
        v-if="slots.length"
        class="space-y-3"
      >
        <AnimatedItem
          v-for="slot in slots"
          :key="slot.month_number"
        >
          <MonthlyReportCard
            :month-slot="slot"
            :can-generate="false"
            :can-view="Boolean(props.canView)"
            :can-download="Boolean(props.canDownload)"
            :show-html-actions="false"
            :empty-message="'Report is not ready yet.'"
            :status-meta="statusMeta(slot)"
            :is-custom="slot.month_number > 3"
            @view="$emit('view-report', slot.report?.id || null)"
            @download-pdf="$emit('download-pdf', slot.report?.id || null)"
          />
        </AnimatedItem>
      </AnimatedList>

      <div
        v-else
        class="rounded-[1.4rem] border border-dashed border-[#d8e4f0] bg-white p-8 text-center text-[0.92rem] text-[#64748b]"
      >
        No monthly reports are available yet.
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import AnimatedList from "@/components/AnimatedList.vue";
import AnimatedItem from "@/components/AnimatedItem.vue";
import MonthlyReportCard from "@/components/reports/MonthlyReportCard.vue";
import type {
  ReportClientCard,
  ReportMonthSlot,
  ReportStatusMeta,
} from "@/types/reports";

const props = defineProps<{
  client: ReportClientCard | null;
  slots: ReportMonthSlot[];
  statusMeta: (slot: ReportMonthSlot) => ReportStatusMeta;
  canView?: boolean;
  canDownload?: boolean;
}>();

defineEmits<{
  (event: "view-report", reportId: number | null): void;
  (event: "download-pdf", reportId: number | null): void;
}>();

const clientInitials = computed(() => {
  const name = props.client?.company_name?.trim();
  if (!name) return "CL";
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  const stopWords = new Set(["the", "of", "and", "for", "in", "to", "a", "an"]);
  const significant = words.filter((w) => !stopWords.has(w.toLowerCase()));
  if (significant.length >= 2) {
    return (significant[0][0] + significant[1][0]).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
});

const generatedCount = computed(() => {
  return props.slots.filter((slot) => {
    const s = String(slot.report?.status || slot.status).toLowerCase();
    return s === "generated" || s === "published";
  }).length;
});

const totalSlots = computed(() => Math.max(props.slots.length, 1));

const progressPercentage = computed(() => {
  return Math.min(100, Math.round((generatedCount.value / totalSlots.value) * 100));
});
</script>
